import * as React from 'react'
import {log} from '@vanguard/logger'
import TagManager from 'react-gtm-module'
import {useRequiredAuth} from '../stores/AuthStore'

import {api} from './api'

import {CreateTrackingLinkRequest} from './api/airbridge/types'
import {AIRBRIDGE_CHANNEL, AIRBRIDGE_URL_SCHEME} from '../constants'

type Status = 'idle' | 'failure'

// ADD NEW DESTINATION/ENDPOINT LOCATIONS HERE *********
const destinations = {
  forms: '/v2/forms/Dashboard.aspx',
  registration: '/ui/forms/application/camper/App',
} as const

type Destination = keyof typeof destinations

interface Return {
  trackingLink: string
  status: Status
  getTrackingLink: (destination: Destination) => Promise<void>
}

export const useCreateTrackingLink = (): Return => {
  const {client} = useRequiredAuth()
  const {ID: clientID} = client
  const [trackingLink, setTrackingLink] = React.useState('')
  const [status, setStatus] = React.useState<Status>('idle')

  const getTrackingLink = React.useCallback<Return['getTrackingLink']>(
    async (destination: Destination) => {
      log.breadcrumb(`Begin Fetch Dynamic Link for ${destination}`)

      const trackingLinkUrl = `${AIRBRIDGE_URL_SCHEME}airbridge-redirect?clientid=${clientID}&redirect=%2F${destination}`

      const redirectURL = `https://${client.citURL}${destinations[destination]}`

      const params = {
        channel: AIRBRIDGE_CHANNEL || '',
        deeplinkUrl: trackingLinkUrl,
        deeplinkOption: {
          showAlertForInitialDeeplinkingIssue: false,
        },
        isReengagement: 'OFF' as CreateTrackingLinkRequest['isReengagement'],
        fallbackPaths: {
          android: redirectURL,
          ios: redirectURL,
          desktop: redirectURL,
        },
      }

      const response = await api.airbridge.getTrackingLink(params)

      if (response.result === 'failure') {
        log.error(new Error(response.error.message))
        setStatus('failure')
      }

      if (response.result === 'success') {
        const {
          data: {shortLink},
        } = response

        setTrackingLink(shortLink)

        setStatus('idle')

        log.breadcrumb(`Generated Tracking Link successfully - ${shortLink}`)
        // Add this data to GTM dataLayer so a GA4 event can use it to track event
        TagManager.dataLayer({
          dataLayer: {
            dynamicLinkType: destination,
            dynamicLinkURL: shortLink,
          },
        })
      }
    },
    [client.citURL, clientID],
  )

  return React.useMemo(
    () => ({
      trackingLink,
      getTrackingLink,
      status,
    }),
    [trackingLink, getTrackingLink, status],
  )
}
